import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlarmClock, Plus, ShieldCheck } from "lucide-react";
import { useState } from "react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { useSession } from "@/hooks/useSession";
import { api, type Application, type Exception_ } from "@/lib/api";
import { cn, fmtAgo, fmtTime } from "@/lib/utils";

const STATUS_VARIANT: Record<string, "success" | "warning" | "secondary" | "destructive"> = {
  active: "success", expired: "secondary", revoked: "warning",
};

function countdown(iso: string): { text: string; urgent: boolean } {
  const s = (new Date(iso).getTime() - Date.now()) / 1000;
  if (s <= 0) return { text: "expired", urgent: false };
  if (s < 3600) return { text: `${Math.ceil(s / 60)}m left`, urgent: true };
  if (s < 86400) return { text: `${Math.floor(s / 3600)}h left`, urgent: s < 7200 };
  return { text: `${Math.floor(s / 86400)}d left`, urgent: false };
}

function scopeLabel(scope: Record<string, string>): string {
  if (scope.path) return `path ${scope.path}`;
  if (scope.arg) return `arg ${scope.arg}`;
  return "global";
}

export default function Exceptions() {
  const qc = useQueryClient();
  const { user } = useSession();
  const canEdit = user?.role === "owner" || user?.role === "operator";
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    app_id: "", rule_id: "", scope_kind: "global", scope_value: "",
    reason: "", expires_in_hours: "24",
  });

  const { data: items } = useQuery({
    queryKey: ["exceptions"],
    queryFn: () => api.get<Exception_[]>("/api/exceptions"),
    refetchInterval: 15_000,
  });
  const { data: apps } = useQuery({
    queryKey: ["apps"],
    queryFn: () => api.get<Application[]>("/api/applications"),
  });
  const appName = Object.fromEntries((apps ?? []).map((a) => [a.id, a.name]));

  const create = useMutation({
    mutationFn: () =>
      api.post("/api/exceptions", {
        app_id: form.app_id,
        rule_id: form.rule_id.trim(),
        control: "waf",
        scope: form.scope_kind === "global" ? {} : { [form.scope_kind]: form.scope_value.trim() },
        reason: form.reason,
        expires_in_hours: Number(form.expires_in_hours),
      }),
    onSuccess: () => {
      setOpen(false);
      setForm({ ...form, rule_id: "", scope_value: "", reason: "" });
      qc.invalidateQueries({ queryKey: ["exceptions"] });
    },
    onError: (e) => setErr(e.message),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.patch(`/api/exceptions/${id}`, { revoke: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exceptions"] }),
  });

  const active = (items ?? []).filter((e) => e.status === "active");

  return (
    <div className="space-y-5">
      <PageHeader
        title={
          <span className="flex items-center gap-2.5">
            WAF Exceptions
            <Badge variant="secondary">{active.length} active</Badge>
          </span>
        }
        description="Time-boxed rule relaxations materialized into the service's modsec-crs config. Expiry is automatic — the worker de-materializes and the original attack blocks again with no manual action."
      >
        {canEdit && (
          <Button size="sm" onClick={() => setOpen(true)} disabled={(apps ?? []).length === 0}>
            <Plus className="h-4 w-4" /> New exception
          </Button>
        )}
      </PageHeader>

      <Card>
        <Table>
          <THead>
            <TR>
              <TH>Status</TH><TH>Rule</TH><TH>Scope</TH><TH>Application</TH>
              <TH>Expires</TH><TH>Reason</TH><TH>By</TH><TH></TH>
            </TR>
          </THead>
          <TBody>
            {(items ?? []).map((e) => {
              const cd = countdown(e.expires_at);
              return (
                <TR key={e.id} className={e.status !== "active" ? "opacity-60" : ""}>
                  <TD><Badge variant={STATUS_VARIANT[e.status] ?? "secondary"}>{e.status}</Badge></TD>
                  <TD className="font-mono text-xs">{e.rule_id}</TD>
                  <TD className="font-mono text-xs">{scopeLabel(e.scope)}</TD>
                  <TD className="text-xs">{appName[e.app_id] ?? e.app_id.slice(0, 8)}</TD>
                  <TD className="text-xs" title={fmtTime(e.expires_at)}>
                    {e.status === "active" ? (
                      <span className={cn("flex items-center gap-1", cd.urgent && "text-amber-300")}>
                        <AlarmClock className="h-3 w-3" />{cd.text}
                      </span>
                    ) : fmtAgo(e.expires_at)}
                  </TD>
                  <TD className="max-w-[220px] truncate text-xs text-muted-foreground" title={e.reason}>
                    {e.reason}
                  </TD>
                  <TD className="text-xs text-muted-foreground">{e.created_by}</TD>
                  <TD className="text-right">
                    {canEdit && e.status === "active" && (
                      <Button size="sm" variant="ghost" disabled={revoke.isPending}
                        onClick={() => revoke.mutate(e.id)}>
                        Revoke
                      </Button>
                    )}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
        {(items ?? []).length === 0 && (
          <EmptyState icon={ShieldCheck} title="No exceptions"
            hint="Create one from a blocked security event, or here directly. Exceptions always expire." />
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} wide
        title="New WAF exception" description="Written into the app's modsec-crs config as a rule removal. Auto-expires — nothing here is permanent.">
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); setErr(""); create.mutate(); }}>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Application</Label>
              <Select value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value })} required>
                <option value="" disabled>select…</option>
                {(apps ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>CRS rule ID</Label>
              <Input value={form.rule_id} onChange={(e) => setForm({ ...form, rule_id: e.target.value })}
                placeholder="942100" required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Scope</Label>
              <Select value={form.scope_kind} onChange={(e) => setForm({ ...form, scope_kind: e.target.value })}>
                <option value="global">whole service</option>
                <option value="path">path prefix</option>
                <option value="arg">argument name</option>
              </Select>
            </div>
            {form.scope_kind !== "global" && (
              <div className="space-y-1.5">
                <Label>{form.scope_kind === "path" ? "Path prefix" : "Argument"}</Label>
                <Input value={form.scope_value}
                  onChange={(e) => setForm({ ...form, scope_value: e.target.value })}
                  placeholder={form.scope_kind === "path" ? "/get" : "q"} required />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Expires in</Label>
              <Select value={form.expires_in_hours} onChange={(e) => setForm({ ...form, expires_in_hours: e.target.value })}>
                <option value="1">1 hour</option>
                <option value="8">8 hours</option>
                <option value="24">24 hours</option>
                <option value="72">3 days</option>
                <option value="168">7 days</option>
                <option value="720">30 days</option>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Reason (audit trail, min 8 chars)</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="False positive on search query param — vendor ticket #123" minLength={8} required />
          </div>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={create.isPending}>{create.isPending ? "Creating…" : "Create exception"}</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
